'use client';
/* eslint-disable jsx-a11y/alt-text */
import Image, { type ImageProps } from 'next/image';
import { DeferredImage } from './deferred-image-picture';
import { AiGeneratedBadge } from '@eliadmin/core/src/components/image/ai-generated-badge';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type ImageWrappedProps = ImageProps & {
  preserveAspectRatio?: 'width' | 'height' | 'both';
  deferUntilNearViewport?: boolean;
};

const getSrcString = (src: ImageProps['src']) => {
  if (!src) return '';
  if (typeof src === 'string') return src;
  if ('src' in src) return src.src;
  if ('default' in src) return src.default.src;
  return '';
};

const hasAiFlag = (src: ImageProps['src']) => {
  const raw = getSrcString(src);
  if (!raw) return false;

  try {
    const url = new URL(raw, 'http://localhost');
    return url.searchParams.get('ai') === '1';
  } catch {
    return /[?&]ai=1(?:&|$)/.test(raw);
  }
};

const getRoundedClassNames = (className?: string) => {
  if (!className) return undefined;
  const tokens = className.split(/\s+/).filter(Boolean);
  const roundedTokens = tokens.filter(token => token === 'rounded' || token.startsWith('rounded-'));
  return roundedTokens.length ? roundedTokens.join(' ') : undefined;
};

const hasBorderClass = (className?: string) => {
  if (!className) return false;
  return className.split(/\s+/).some(token => token === 'border' || token.startsWith('border-'));
};

const FIXED_SIZE_PATTERN = /^(\d+(?:\.\d+)?)(px|rem)$/;

const parseFixedSlotSize = (value: string) => {
  const match = value.trim().match(FIXED_SIZE_PATTERN);
  if (!match) return null;

  const numericValue = Number(match[1]);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;

  return Math.round(match[2] === 'rem' ? numericValue * 16 : numericValue);
};

const parseMaxFixedImageSize = (sizes?: string) => {
  if (!sizes) return null;

  const candidates = sizes
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const lastSpaceIndex = part.lastIndexOf(' ');
      const slotSize = lastSpaceIndex === -1 ? part : part.slice(lastSpaceIndex + 1);
      return parseFixedSlotSize(slotSize);
    });

  if (!candidates.length || candidates.some(value => value === null)) {
    return null;
  }

  return Math.max(...candidates.filter((value): value is number => value !== null));
};

const isZeroDimension = (value: ImageProps['width'] | ImageProps['height']) => {
  return value === 0 || value === '0';
};

const getTailwindUtilityToken = (token: string) => {
  const normalizedToken = token.startsWith('!') ? token.slice(1) : token;
  const variantSeparatorIndex = normalizedToken.lastIndexOf(':');
  return variantSeparatorIndex === -1
    ? normalizedToken
    : normalizedToken.slice(variantSeparatorIndex + 1);
};

const controlsWidth = (token: string) => {
  return (
    token.startsWith('w-') ||
    token.startsWith('min-w-') ||
    token.startsWith('max-w-') ||
    token.startsWith('size-')
  );
};

const controlsHeight = (token: string) => {
  return (
    token.startsWith('h-') ||
    token.startsWith('min-h-') ||
    token.startsWith('max-h-') ||
    token.startsWith('size-')
  );
};

const getCssControlledDimensions = (className?: string) => {
  const tokens = className?.split(/\s+/).filter(Boolean) ?? [];
  const utilities = tokens.map(getTailwindUtilityToken);

  return {
    widthControlled: utilities.some(controlsWidth),
    heightControlled: utilities.some(controlsHeight),
  };
};

const getAspectRatioMode = (
  props: ImageProps,
  preserveAspectRatio?: ImageWrappedProps['preserveAspectRatio'],
) => {
  if (preserveAspectRatio) return preserveAspectRatio;

  const { widthControlled, heightControlled } = getCssControlledDimensions(
    typeof props.className === 'string' ? props.className : undefined,
  );

  if (widthControlled === heightControlled) return undefined;
  return widthControlled ? 'height' : 'width';
};

const preserveAspectRatioStyle = (
  props: ImageProps,
  preserveAspectRatio?: ImageWrappedProps['preserveAspectRatio'],
) => {
  const aspectRatioMode = getAspectRatioMode(props, preserveAspectRatio);

  if (!aspectRatioMode || props.fill) return props;

  return {
    ...props,
    style: {
      ...props.style,
      ...((aspectRatioMode === 'width' || aspectRatioMode === 'both') && {
        width: 'auto',
      }),
      ...((aspectRatioMode === 'height' || aspectRatioMode === 'both') && {
        height: 'auto',
      }),
    },
  };
};

export const ImageWrapped = (props: ImageWrappedProps) => {
  const { preserveAspectRatio, deferUntilNearViewport, ...baseProps } = props;
  const { fill } = baseProps;
  const showAiBadge = hasAiFlag(baseProps.src);
  const fixedSize = parseMaxFixedImageSize(baseProps.sizes);
  const shouldFallbackToSizes =
    !fill &&
    fixedSize !== null &&
    isZeroDimension(baseProps.width) &&
    isZeroDimension(baseProps.height);

  const imageProps = preserveAspectRatioStyle(
    // Keep the caller's rendered slot for responsive source selection.
    shouldFallbackToSizes
      ? { ...baseProps, width: fixedSize, height: fixedSize, sizes: undefined }
      : baseProps,
    preserveAspectRatio,
  );

  const image = deferUntilNearViewport ? (
    <DeferredImage {...imageProps} />
  ) : (
    <Image {...imageProps} />
  );
  if (!showAiBadge) return image;

  const roundedClassName = getRoundedClassNames(baseProps.className);
  const shouldClip = Boolean(roundedClassName) || hasBorderClass(baseProps.className);
  const wrapperClassName = cn(
    fill ? 'relative block h-full w-full' : 'relative inline-block',
    shouldClip && 'overflow-hidden',
    roundedClassName,
  );

  return (
    <span className={wrapperClassName}>
      {image}
      <AiGeneratedBadge />
    </span>
  );
};

export default ImageWrapped;
